import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { Home, TrendingUp, PieChart, Lightbulb, Building, IndianRupee, MapPin, Key } from 'lucide-react';

const InteractiveHousePlanner = () => {
  const [inputs, setInputs] = useState({
    propertyValue: 10000000,
    downPaymentPercent: 20,
    yearsToPurchase: 5,
    expectedReturn: 12,
    currentSavings: 0,
    propertyAppreciation: 8
  });
  const [result, setResult] = useState(null);

  const calculateHomeFund = useCallback(() => {
    const propertyValue = parseFloat(inputs.propertyValue);
    const downPaymentPercent = parseFloat(inputs.downPaymentPercent) / 100;
    const years = parseInt(inputs.yearsToPurchase);
    const currentSavings = parseFloat(inputs.currentSavings || 0);
    const propertyAppreciation = parseFloat(inputs.propertyAppreciation) / 100;
    const r = parseFloat(inputs.expectedReturn) / 12 / 100;
    const n = years * 12;

    const futurePropertyValue = propertyValue * Math.pow(1 + propertyAppreciation, years);
    const futureDownPayment = futurePropertyValue * downPaymentPercent;
    const target = Math.max(futureDownPayment - currentSavings, 0);

    const registrationCost = futurePropertyValue * 0.08;
    const totalRequired = target + registrationCost;

    if (r === 0 || n === 0) {
      setResult({
        monthlySIP: Math.round(totalRequired / Math.max(n, 1)),
        totalInvested: Math.round(totalRequired),
        wealthGained: 0,
        futurePropertyValue: Math.round(futurePropertyValue),
        futureDownPayment: Math.round(futureDownPayment),
        registrationCost: Math.round(registrationCost),
        totalRequired: Math.round(totalRequired),
        loanAmount: Math.round(futurePropertyValue * (1 - downPaymentPercent)),
        yearsToGoal: years
      });
      return;
    }

    const monthlySIP = totalRequired * r / (((Math.pow(1 + r, n) - 1)) * (1 + r));
    const totalInvested = monthlySIP * n;
    const wealthGained = totalRequired - totalInvested;

    setResult({
      monthlySIP: Math.round(monthlySIP),
      totalInvested: Math.round(totalInvested),
      wealthGained: Math.round(wealthGained),
      futurePropertyValue: Math.round(futurePropertyValue),
      futureDownPayment: Math.round(futureDownPayment),
      registrationCost: Math.round(registrationCost),
      totalRequired: Math.round(totalRequired),
      loanAmount: Math.round(futurePropertyValue * (1 - downPaymentPercent)),
      yearsToGoal: years
    });
  }, [inputs]);

  useEffect(() => {
    calculateHomeFund();
  }, [calculateHomeFund]);

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const propertyPresets = [
    { name: '1 BHK Apartment', value: 5000000 },
    { name: '2 BHK Apartment', value: 10000000 },
    { name: '3 BHK Apartment', value: 20000000 },
    { name: 'Villa / Independent', value: 50000000 }
  ];

  const sliderStyles = `
    .slider-emerald::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #10b981, #14b8a6);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
    }
    .slider-emerald::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #10b981, #14b8a6);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
      border: none;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl mb-6">
              <Home className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              House Planning Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Plan your dream home purchase. Calculate the down payment you need and start saving systematically!
            </p>
          </div>

          <div className="mb-8">
            <p className="text-white/60 text-sm mb-3 text-center">Select Property Type:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {propertyPresets.map((preset, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputs({ ...inputs, propertyValue: preset.value })}
                  className={`px-4 py-2 border rounded-full text-sm transition-all ${
                    inputs.propertyValue === preset.value
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <Building className="w-6 h-6 text-emerald-400" />
                Property Details
              </h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Current Property Value</label>
                    <span className="text-emerald-400 font-bold text-xl">{formatCurrency(inputs.propertyValue)}</span>
                  </div>
                  <input
                    type="range"
                    min="2000000"
                    max="100000000"
                    step="500000"
                    value={inputs.propertyValue}
                    onChange={(e) => setInputs({...inputs, propertyValue: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-emerald"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>₹20 L</span>
                    <span>₹10 Cr</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Down Payment %</label>
                    <span className="text-teal-400 font-bold text-xl">{inputs.downPaymentPercent}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    step="5"
                    value={inputs.downPaymentPercent}
                    onChange={(e) => setInputs({...inputs, downPaymentPercent: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-emerald"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>10%</span>
                    <span>50%</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Years to Purchase</label>
                    <span className="text-cyan-400 font-bold text-xl">{inputs.yearsToPurchase} Years</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="15"
                    step="1"
                    value={inputs.yearsToPurchase}
                    onChange={(e) => setInputs({...inputs, yearsToPurchase: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-emerald"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1 Year</span>
                    <span>15 Years</span>
                  </div>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Current Savings</label>
                  <input
                    type="number"
                    value={inputs.currentSavings}
                    onChange={(e) => setInputs({...inputs, currentSavings: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    placeholder="0"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-white text-sm">Property Appreciation</label>
                      <span className="text-orange-400 font-bold">{inputs.propertyAppreciation}%</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="15"
                      step="1"
                      value={inputs.propertyAppreciation}
                      onChange={(e) => setInputs({...inputs, propertyAppreciation: e.target.value})}
                      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-white text-sm">Expected Return</label>
                      <span className="text-green-400 font-bold">{inputs.expectedReturn}%</span>
                    </div>
                    <input
                      type="range"
                      min="8"
                      max="15"
                      step="0.5"
                      value={inputs.expectedReturn}
                      onChange={(e) => setInputs({...inputs, expectedReturn: e.target.value})}
                      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>

            {result && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-xl rounded-3xl p-8 border border-emerald-500/30">
                  <p className="text-white/60 text-sm mb-2">Down Payment Fund</p>
                  <div className="flex items-center gap-3 mb-4">
                    <IndianRupee className="w-6 h-6 text-emerald-400" />
                    <h3 className="text-white/70 text-sm">Monthly SIP Required</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.monthlySIP)}</p>
                  <p className="text-white/50 text-sm">for {result.yearsToGoal} years</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <p className="text-white font-semibold mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400" />
                    Future Property Details
                  </p>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/60">Future Property Value</span>
                      <span className="text-white font-semibold">{formatCurrency(result.futurePropertyValue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Down Payment ({inputs.downPaymentPercent}%)</span>
                      <span className="text-emerald-400 font-semibold">{formatCurrency(result.futureDownPayment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Registration &amp; Stamp Duty</span>
                      <span className="text-orange-400 font-semibold">{formatCurrency(result.registrationCost)}</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between">
                      <span className="text-white">Total You Need</span>
                      <span className="text-white font-bold text-lg">{formatCurrency(result.totalRequired)}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-white/50" />
                      <p className="text-white/50 text-xs">Your Investment</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(result.totalInvested)}</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <p className="text-white/50 text-xs">Returns Earned</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(result.wealthGained)}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Key className="w-5 h-5 text-blue-400" />
                    <p className="text-blue-400 font-semibold">Home Loan Estimate</p>
                  </div>
                  <p className="text-3xl font-bold text-white mb-2">{formatCurrency(result.loanAmount)}</p>
                  <p className="text-white/60 text-sm">Remaining {100 - inputs.downPaymentPercent}% to be financed through home loan</p>
                </div>

                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-semibold mb-2">Home Buying Strategy</p>
                      <p className="text-white/80 text-sm">
                        {inputs.downPaymentPercent >= 30 
                          ? "Great! A higher down payment means lower EMI burden and interest outgo. You're on the right track!"
                          : inputs.yearsToPurchase <= 3
                          ? "With a shorter timeline, focus on capital protection. Consider short-term debt funds or FDs for stability."
                          : "Consider increasing your down payment to 25-30% to reduce your home loan burden and save on interest."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] transition-all duration-300"
            >
              Get Expert Home Buying Advice
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveHousePlanner;
